import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { AriaOrb } from "./AriaOrb";
import { CommandPalette } from "./CommandPalette";
import {
  Home, MessageSquare, GraduationCap, StickyNote, FlaskConical, Mic2, Network,
  CalendarDays, Search, Youtube, BookOpen, Settings, LogOut, Moon, Sun, AlertTriangle, Timer, Briefcase, Rocket, Menu, X,
} from "lucide-react";
import { useEffect, useLayoutEffect, useState } from "react";
import GuidedTour from "./GuidedTour";
import { NishuWidget } from "./NishuWidget";
import { toast } from "sonner";

const MAIN_NAV = [
  { to: "/app/home", icon: Home, label: "Home" },
  { to: "/app/chat", icon: MessageSquare, label: "Ask ARIA" },
  { to: "/app/learn", icon: GraduationCap, label: "Learn" },
  { to: "/app/notes", icon: StickyNote, label: "Notes" },
  { to: "/app/documents", icon: FlaskConical, label: "Research" },
  { to: "/app/interviews", icon: Mic2, label: "Interviews" },
  { to: "/app/knowledge", icon: Network, label: "Knowledge" },
  { to: "/app/plan", icon: Timer, label: "Countdown" },
  { to: "/app/applications", icon: Briefcase, label: "Applications" },
  { to: "/app/calendar", icon: CalendarDays, label: "Calendar" },
  { to: "/app/search", icon: Search, label: "Search" },
];

const CONNECT_NAV = [
  { to: "/app/setup", icon: Rocket, label: "Course setup" },
  { to: "/app/learn", icon: Youtube, label: "YouTube" },
  { to: "/app/turbolearn", icon: BookOpen, label: "TurboLearn" },
  { to: "/app/notes", icon: StickyNote, label: "Notes" },
];

export default function AppLayout() {
  const { user, org, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [mobileNav, setMobileNav] = useState(false);
  const [unreadComments, setUnreadComments] = useState(0);
  useEffect(() => {
    const load = () => api.get("/advisor/unread").then(r => setUnreadComments(r.data.total || 0)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("aria-comments-read", load);
    return () => { clearInterval(t); window.removeEventListener("aria-comments-read", load); };
  }, []);
  const [dark, setDark] = useState(() => {
    const v = localStorage.getItem("aria_dark");
    return v === null ? true : v === "1";   // dark-first canonical
  });

  useLayoutEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    document.documentElement.dataset.theme = dark ? "dark" : "light";
    localStorage.setItem("aria_dark", dark ? "1" : "0");
  }, [dark]);

  const accent = org?.accent_color || "#2563FF";
  const nickname = org?.aria_nickname || "ARIA";

  const onLogout = async () => { await logout(); navigate("/login"); };
  const onEndImpersonation = async () => {
    try {
      await api.post("/admin/end-impersonation");
      toast.success("Returned to super admin");
      if (checkAuth) await checkAuth();
      navigate("/admin");
    } catch { toast.error("Failed to end impersonation"); }
  };

  return (
    <div className="h-dvh overflow-hidden flex flex-col bg-[var(--aria-bg)] text-[var(--aria-text)]">
      <CommandPalette />
      {user?.imp_by && (
        <div data-testid="impersonation-banner"
          className="w-full bg-[var(--aria-accent)] text-[#18181B] text-sm py-2 px-4 flex items-center justify-between gap-3 sticky top-0 z-50 border-b border-[var(--aria-primary-hover)]">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} strokeWidth={2} />
            <span>
              <strong className="font-display">Impersonating</strong> {user.name} <span className="font-mono text-xs opacity-80">({user.email})</span> · returning to <span className="font-mono text-xs">{user.imp_by_email}</span>
            </span>
          </div>
          <button onClick={onEndImpersonation} data-testid="end-impersonation-button"
            className="btn px-3 py-1 text-[11px] uppercase tracking-[0.1em] rounded-md bg-[#18181B] text-white hover:bg-black font-medium">
            End impersonation
          </button>
        </div>
      )}
      <div className="lg:hidden h-[49px] shrink-0 flex items-center gap-3 px-3 border-b border-[var(--aria-border)]">
        <button type="button" data-testid="mobile-navigation-toggle" aria-label={mobileNav ? "Close navigation" : "Open navigation"} onClick={() => setMobileNav(v => !v)} className="p-2">{mobileNav ? <X size={19} /> : <Menu size={19} />}</button>
        <span data-testid="mobile-brand" className="font-display">{nickname}</span>
        <button type="button" data-testid="mobile-theme-toggle" title={dark ? "Switch to light mode" : "Switch to dark mode"}
          aria-label={dark ? "Switch to light mode" : "Switch to dark mode"} onClick={() => setDark(v => !v)}
          className="ml-auto p-2 rounded-md hover:bg-[var(--aria-surface-2)] text-[var(--aria-text-muted)] transition-colors">{dark ? <Sun size={18} /> : <Moon size={18} />}</button>
      </div>
      <div className="flex flex-1 min-h-0 min-w-0">
      {mobileNav && <button type="button" data-testid="mobile-navigation-dismiss" aria-label="Close navigation" onClick={() => setMobileNav(false)} className="lg:hidden fixed inset-0 top-[49px] z-30 bg-black/60" />}
      <aside onClick={event => { if (event.target.closest("a")) setMobileNav(false); }} className={`${mobileNav ? "flex fixed top-[49px] bottom-0 left-0 z-40 overflow-y-auto" : "hidden"} lg:flex lg:static min-h-0 w-64 shrink-0 border-r border-[var(--aria-border)] bg-[var(--aria-surface)] flex-col`}>
        <div className="px-5 py-5 flex items-center gap-3 border-b border-[var(--aria-border)]">
          <AriaOrb size={36} color={accent} />
          <div className="min-w-0 flex-1">
            <div className="font-display text-lg leading-none tracking-tight">{nickname}</div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-[var(--aria-text-muted)] truncate mt-1">Dr. Shankar</div>
          </div>
          <button type="button" data-testid="sidebar-theme-toggle" title={dark ? "Switch to light mode" : "Switch to dark mode"}
            aria-label={dark ? "Switch to light mode" : "Switch to dark mode"} onClick={() => setDark(v => !v)}
            className="shrink-0 p-2 rounded-md hover:bg-[var(--aria-surface-2)] text-[var(--aria-text-muted)] transition-colors">{dark ? <Sun size={18} /> : <Moon size={18} />}</button>
        </div>

        <nav className="flex-1 py-3 space-y-px overflow-y-auto">
          {MAIN_NAV.map(n => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            >
              <n.icon size={16} strokeWidth={1.6} />
              <span>{n.label}</span>
              {n.to === "/app/interviews" && unreadComments > 0 && (
                <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-400 text-[#030711] shadow-[0_0_8px_#34d399]" data-testid="nav-interviews-unread">{unreadComments}</span>
              )}
            </NavLink>
          ))}

          <div className="px-5 pt-5 pb-1.5 text-[10px] uppercase tracking-[0.2em] text-[var(--aria-text-faint)] font-mono">Connect</div>
          {CONNECT_NAV.map(n => (
            <NavLink
              key={`connect-${n.label}`}
              to={n.to}
              data-testid={`nav-connect-${n.label.toLowerCase()}`}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            >
              <n.icon size={16} strokeWidth={1.6} />
              <span>{n.label}</span>
            </NavLink>
          ))}

          {user?.role === "super_admin" && (
            <>
              <div className="px-5 pt-5 pb-1.5 text-[10px] uppercase tracking-[0.2em] text-[var(--aria-text-faint)] font-mono">Platform</div>
              <NavLink to="/admin" data-testid="nav-super-admin" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <Settings size={16} strokeWidth={1.6} /><span>Super Admin</span>
              </NavLink>
            </>
          )}
        </nav>
        <div className="p-3 border-t border-[var(--aria-border)] space-y-1">
          <NavLink to="/app/settings" data-testid="nav-settings"
            className={({ isActive }) => `w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-[var(--aria-surface-2)] transition-colors ${isActive ? "text-[var(--aria-primary)]" : "text-[var(--aria-text-muted)]"}`}>
            <Settings size={15} /> Settings
          </NavLink>
          <button
            onClick={() => setDark(!dark)}
            data-testid="toggle-dark-mode"
            aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-[var(--aria-surface-2)] text-[var(--aria-text-muted)] transition-colors"
          >
            {dark ? <Sun size={15} /> : <Moon size={15} />}
            {dark ? "Light mode" : "Dark mode"}
          </button>
          <div className="px-3 py-2 text-xs border-t border-[var(--aria-border)] mt-2 pt-3">
            <div className="font-medium text-[var(--aria-text)] truncate">{user?.name}</div>
            <div className="text-[var(--aria-text-muted)] truncate">{user?.email}</div>
            <div className="mt-1.5 aria-pill aria-pill-primary">{user?.role?.replace("_", " ")}</div>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-[var(--aria-danger)] hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-y-auto">
        <Outlet />
      </main>
      </div>
      <GuidedTour />
      <NishuWidget />
    </div>
  );
}
